import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import * as Dialog from "@radix-ui/react-dialog";
import { useOnboardingStore } from "../stores/onboarding-store";
import { useBrands } from "@/modules/brand";
import { CHECKLIST_ITEMS, SECONDARY_CHECKLIST_ITEMS, type OnboardingItemKey } from "../types";

const ITEM_ILLUSTRATIONS: Record<OnboardingItemKey, string> = {
	customize_branding: "/images/onboarding/icon-customize-branding.png",
	create_form: "/images/onboarding/icon-create-form.png",
	share_form: "/images/onboarding/icon-share-form.png",
	collect_testimonial: "/images/onboarding/icon-collect-testimonial.png",
	setup_widget: "/images/onboarding/icon-setup-widget.png",
	embed_on_website: "/images/onboarding/icon-embed-widget.png",
};

export default function OnboardingTiles() {
	const { brandUrl } = useParams();
	const navigate = useNavigate();
	const { completedSteps, loading, fetch, completeStep, isDismissed } = useOnboardingStore();
	const { selectedBrand } = useBrands();
	const [demoVideoOpen, setDemoVideoOpen] = useState(false);

	const workspaceId = brandUrl;

	const canRemoveBranding = !!(selectedBrand?.can_disable_branding);

	useEffect(() => {
		if (workspaceId) {
			fetch(workspaceId);
		}
	}, [workspaceId, fetch]);

	// Auto-complete remove_branding for existing users on a plan without branding
	useEffect(() => {
		if (canRemoveBranding && workspaceId && !completedSteps.includes("remove_branding")) {
			completeStep(workspaceId, "remove_branding");
		}
	}, [canRemoveBranding, workspaceId, completedSteps, completeStep]);

	if (loading || !workspaceId) return null;

	const getSecondaryRoute = (key: string, defaultRoute?: string) => {
		if (key === "remove_branding") return canRemoveBranding ? "settings" : "billing";
		return defaultRoute;
	};

	if (isDismissed(workspaceId)) return null;

	return (
		<div className="mb-10">
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
				{CHECKLIST_ITEMS.map((item) => {
					const done = completedSteps.includes(item.key);
					return (
						<button
							key={item.key}
							onClick={() => navigate(`/${brandUrl}/${item.route}`)}
							className="relative group flex flex-col items-center gap-3 p-8 rounded-xl bg-white border border-gray-200 text-center transition-all cursor-pointer hover:border-gray-300 hover:shadow-sm overflow-hidden"
						>
							{/* Dog-ear completion badge */}
							{done && (
								<div className="absolute top-0 right-0">
									<svg width="36" height="36" viewBox="0 0 36 36" fill="none">
										<path d="M0 0H36V36L0 0Z" fill="#111827" />
										<path d="M21 11l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</div>
							)}

							{/* Illustration */}
							<img src={ITEM_ILLUSTRATIONS[item.key]} alt={item.label} className="w-24 h-24 object-contain" />

							{/* Label & description */}
							<div>
								<p className="font-medium text-sm text-primary">{item.label}</p>
								<p className="text-xs mt-1 text-tertiary">{item.description}</p>
							</div>
						</button>
					);
				})}
			</div>

			<div className="my-6 flex items-center gap-4">
				<div className="flex-1 h-px bg-gray-200" />
				<span className="text-xs font-medium text-tertiary uppercase tracking-widest">Explore more</span>
				<div className="flex-1 h-px bg-gray-200" />
			</div>

			{/* Secondary items */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
				{SECONDARY_CHECKLIST_ITEMS.map((item) => {
					const done = completedSteps.includes(item.key);
					return item.key === "watch_demo_video" ? (
						<button
							key={item.key}
							onClick={() => {
								setDemoVideoOpen(true);
								if (workspaceId) completeStep(workspaceId, item.key);
							}}
							className="relative group flex items-center justify-between p-4 rounded-xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left overflow-hidden"
						>
							{done && (
								<div className="absolute top-0 right-0">
									<svg width="28" height="28" viewBox="0 0 28 28" fill="none">
										<path d="M0 0H28V28L0 0Z" fill="#111827" />
										<path d="M16 9.5l1.5 1.5 3-3" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</div>
							)}
							<div>
								<p className="text-sm font-medium text-tertiary group-hover:text-primary transition-colors">{item.label}</p>
								<p className="text-xs text-gray-400 mt-0.5">{item.description}</p>
							</div>
						</button>
					) : (
						<button
							key={item.key}
							onClick={() => navigate(`/${brandUrl}/${getSecondaryRoute(item.key, item.route)}`)}
							className="relative group flex items-center justify-between p-4 rounded-xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left overflow-hidden"
						>
							{done && (
								<div className="absolute top-0 right-0">
									<svg width="28" height="28" viewBox="0 0 28 28" fill="none">
										<path d="M0 0H28V28L0 0Z" fill="#111827" />
										<path d="M16 9.5l1.5 1.5 3-3" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</div>
							)}
							<div>
								<p className="text-sm font-medium text-tertiary group-hover:text-primary transition-colors">{item.label}</p>
								<p className="text-xs text-gray-400 mt-0.5">
									{item.key === "remove_branding" && canRemoveBranding
										? "Your plan supports custom branding. Go to settings to configure it."
										: item.description}
								</p>
							</div>
						</button>
					);
				})}
			</div>

			<p className="mt-4 text-sm text-tertiary text-center">
				Have questions or ideas?{" "}
				<a href="https://sayaboutus.featurebase.app/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary underline hover:no-underline">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="shrink-0">
						<path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-11.883 4.008C4.028 14.88 3.75 13.468 3.75 12c0-4.556 3.694-8.25 8.25-8.25s8.25 3.694 8.25 8.25-3.694 8.25-8.25 8.25a8.21 8.21 0 01-3.163-.627l-3.712 1.027 1.027-3.712a8.17 8.17 0 01-.16-.922z" />
					</svg>
					Send us your feedback
				</a>
			</p>
		<Dialog.Root open={demoVideoOpen} onOpenChange={setDemoVideoOpen}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
					<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-3xl">
						<Dialog.Title className="sr-only">Platform Overview</Dialog.Title>
						<div className="relative aspect-video rounded-xl overflow-hidden bg-black">
							<iframe
								allowFullScreen
								width="100%"
								height="100%"
								frameBorder="0"
								title="Say About Us - Platform Overview"
								allow="picture-in-picture"
								src={demoVideoOpen ? "https://shashanksn.neetorecord.com/embeds/c303cc01-76dc-42f3-82fc-3b076136a5c5" : undefined}
								className="absolute inset-0 w-full h-full"
							/>
						</div>
						<Dialog.Close className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
								<path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
							</svg>
						</Dialog.Close>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</div>
	);
}
